import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, int, num } from "../../utils/xlsx";
import {
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  ratio,
  REPORTS,
  sheetHeading,
  signTone,
  StatCard,
  validPeriod,
} from "./common";

/** Owner's one-screen view: profit, cash, working capital, receivables / payables, cash flow and key ratios. */
const FinancialSummary = ({ onNavigate }) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setData(await api.get(`${REPORTS}/dashboard`, { fromDate: period.fromDate, toDate: period.toDate }));
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load financial summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = data
    ? [
        {
          title: "Profitability",
          page: "profitLossReport",
          cards: [
            { label: "Net Sales", value: data.netSales },
            { label: "Gross Profit", value: data.grossProfit, hint: `${ratio(data.grossProfitPercent, "%")} of sales`, tone: signTone(data.grossProfit) },
            { label: "Net Profit", value: data.netProfit, hint: `${ratio(data.netProfitPercent, "%")} of sales`, tone: signTone(data.netProfit) },
            { label: "Closing Stock", value: data.closingStock },
          ],
        },
        {
          title: "Cash & Bank",
          page: "cashBankReport",
          cards: [
            { label: "Cash in Hand", value: data.cashInHand },
            { label: "Bank Accounts", value: data.bankBalance },
            { label: "Bank OD / CC Used", value: data.bankOdOutstanding },
          ],
        },
        {
          title: "Cash Flow",
          page: "cashFlowReport",
          cards: [
            { label: "Cash Inflow", value: data.cashInflow, tone: "good" },
            { label: "Cash Outflow", value: data.cashOutflow, tone: "bad" },
            { label: "Net Cash Flow", value: data.netCashFlow, tone: signTone(data.netCashFlow) },
          ],
        },
        {
          title: "Receivables & Payables",
          page: "receivablesReport",
          cards: [
            { label: "Receivables", value: data.receivables },
            { label: "Overdue Receivables", value: data.overdueReceivables, tone: Number(data.overdueReceivables) > 0 ? "bad" : undefined },
            { label: "Payables", value: data.payables },
            { label: "Overdue Payables", value: data.overduePayables, tone: Number(data.overduePayables) > 0 ? "bad" : undefined },
          ],
        },
        {
          title: "Working Capital",
          page: "workingCapitalReport",
          cards: [
            { label: "Current Assets", value: data.currentAssets },
            { label: "Current Liabilities", value: data.currentLiabilities },
            { label: "Working Capital", value: data.workingCapital, tone: signTone(data.workingCapital) },
            { label: "Current Ratio", text: ratio(data.currentRatio), raw: data.currentRatio },
          ],
        },
        {
          title: "Ratios",
          page: "ratioAnalysisReport",
          cards: [
            { label: "Inventory Turnover", text: ratio(data.inventoryTurnover, " times"), raw: data.inventoryTurnover, hint: `${ratio(data.inventoryDays)} days of stock` },
            { label: "Receivable Turnover", text: ratio(data.receivableTurnover, " times"), raw: data.receivableTurnover, hint: `${ratio(data.receivableDays)} days to collect` },
            { label: "Debt to Equity", text: ratio(data.debtToEquity), raw: data.debtToEquity },
            { label: "Return on Investment", text: ratio(data.returnOnInvestmentPercent, "%"), raw: data.returnOnInvestmentPercent },
          ],
        },
      ]
    : [];

  const exportExcel = () => {
    const rows = [...sheetHeading("Financial Summary", periodLabel(applied)), [bold("Section"), bold("Metric"), bold("Value")]];
    sections.forEach((section) =>
      section.cards.forEach((card) => rows.push([section.title, card.label, card.text !== undefined ? num(card.raw) : num(card.value)]))
    );
    rows.push([], ["Period days", "", int(Math.round((new Date(applied.toDate) - new Date(applied.fromDate)) / 86400000) + 1)]);
    download(`Financial Summary ${periodLabel(applied)}`, [{ name: "Financial Summary", rows, widths: [26, 30, 18] }]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Headline figures from the books for the selected period. Balances (cash, receivables, working capital) are as on the period's last day.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!data} />
      </div>

      {!data ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        sections.map((section) => (
          <div key={section.title} style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
              <h3 style={{ fontSize: "15px", fontWeight: 700 }}>{section.title}</h3>
              {onNavigate ? (
                <button type="button" className="secondary" onClick={() => onNavigate(section.page)}>
                  View report
                </button>
              ) : null}
            </div>
            <div className="stat-grid">
              {section.cards.map((card) => (
                <StatCard key={card.label} label={card.label} value={card.text !== undefined ? card.text : `₹${money(card.value)}`} hint={card.hint} tone={card.tone} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default FinancialSummary;
